<script setup lang="ts">
import { computed, watch, nextTick } from 'vue';
import { useForm, useFieldValue } from 'vee-validate';
import { omit } from 'lodash-es';
import {
  commercialIncomingPropertyTypeOptions,
  commercialOutgoingPropertyTypeOptions,
  commercialTypeOptions,
  propertyUsedOptions,
  propertyDetailOptions,
  PropertyDetailOption,
  PropertyType,
  PropertyPurpose,
  LoanType,
  residenceTypeOptions,
  residentialCommercialTypes,
  quoteCommercialTypeOptions,
} from '@/helpers/const';
import { useBrokerLoanAppProcess } from '@/helpers/useBrokerLoanAppProcess';
import { brokerPropertyDetailsSchema } from '@/validation';

import PropertyToPurchaseFields from './PropertyToPurchaseFields.vue';
import PropertyToSellFields from './PropertyToSellFields.vue';
import SupportingPropertyFields from './SupportingPropertyFields.vue';
import IncomingPropertyReceiveRentalIncome from '@/components/application/incoming-property-receive-rental-income.vue';
import IncomingPropertyExpectedRentalIncome from '@/components/application/incoming-property-expected-rental-income.vue';

import type { PropertyDetailOptionType } from '@/helpers/const';
import type { PropertyDetailEntry } from '@/types';

const props = defineProps<{
  modelValue: PropertyDetailEntry;
  hasIncomingProperty?: boolean;
  defaultPropertyOptions?: PropertyDetailOptionType;
  isPropertyOptionsHidden?: boolean;
}>();

const { loanApp } = useBrokerLoanAppProcess();

const loanType = computed(() => loanApp.value.loanType);

const emit = defineEmits<{
  (e: 'update:modelValue', value: PropertyDetailEntry): void;
}>();

const { meta, submitCount, values, handleSubmit, resetForm, setFieldValue } = useForm<PropertyDetailEntry>({
  validateOnMount: false,
  validationSchema: brokerPropertyDetailsSchema,
});

const propertyOptionsField = useFieldValue<string>('propertyOptions');
const idField = useFieldValue<string>('id');

const prefixMap: Record<string, string> = {
  [PropertyDetailOption.PROPERTY_TO_PURCHASE]: 'incoming-property',
  [PropertyDetailOption.PROPERTY_TO_SELL]: 'outgoing-property',
  [PropertyDetailOption.SUPPORTING_PROPERTY]: 'supporting-property',
};

const baseId = computed(() => {
  const index = idField.value?.split('-').pop() ?? '0';
  const prefix = prefixMap[propertyOptionsField.value ?? ''] ?? 'property';
  return `${prefix}-${index}`;
});

// Property to purchase with vacant land or retirement home requires a single security loan application
const isPropertyPurchaseRestricted = computed(() => {
  return (
    values.propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE &&
    (values.propertyType === PropertyType.VACANT_LAND ||
      values.propertyType === PropertyType.RETIREMENT_LAND_OR_COMMUNITY_HOME)
  );
});

const propertyOptions = computed(() => values.propertyOptions);

const propertyOptionTooltips: Record<string, string> = {
  [PropertyDetailOption.SUPPORTING_PROPERTY]:
    "A 'Supporting Property' is an existing property that isn't being bought or sold, but is used as additional security for Loan To Value Ratio purposes.",
};

const commercialTypeTooltips: Record<string, string> = {
  SPECIALISED_USE: 'Specialised Use includes childcare, gaming zone, medical clinic, and service stations.',
};

const availablePropertyOptions = computed(() => {
  const isSingleSecurity = loanType.value === LoanType.SINGLE_SECURITY;
  const isCurrentlyPurchase = values.propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE;
  const shouldHidePurchase = isSingleSecurity || (props.hasIncomingProperty && !isCurrentlyPurchase);

  return shouldHidePurchase
    ? omit(propertyDetailOptions, PropertyDetailOption.PROPERTY_TO_PURCHASE)
    : propertyDetailOptions;
});

const propertyTypeOptions = computed(() => {
  const isPurchase = values.propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE;
  return isPurchase ? commercialIncomingPropertyTypeOptions : commercialOutgoingPropertyTypeOptions;
});

const isResidentialCommercialType = computed(
  () =>
    values.propertyType === PropertyType.COMMERCIAL &&
    residentialCommercialTypes.includes(values.commercialType as (typeof residentialCommercialTypes)[number]),
);

const showPropertyLeasedQuestion = computed(
  () => !isPropertyPurchaseRestricted.value && values.propertyPurpose === PropertyPurpose.INVESTMENT_PROPERTY,
);

const showRentalForSupportingProperty = computed(
  () =>
    values.propertyOptions === PropertyDetailOption.SUPPORTING_PROPERTY &&
    values.propertyPurpose === PropertyPurpose.INVESTMENT_PROPERTY,
);

const update = handleSubmit(
  () => {
    if (isPropertyPurchaseRestricted.value) {
      return false;
    }
    emit('update:modelValue', { ...values });
    return true;
  },
  () => false,
);

const commercialOptions = computed(() => {
  // Currently Support Property is following the quote commercial type options instead of the commercial type options
  return values.propertyOptions === PropertyDetailOption.SUPPORTING_PROPERTY
    ? quoteCommercialTypeOptions
    : commercialTypeOptions;
});

const cancel = () => {
  resetForm();
};

watch(
  () => props.modelValue,
  (val) => {
    const initialValues = { ...val };
    // Apply default propertyOptions if not already set
    if (!initialValues.propertyOptions && props.defaultPropertyOptions) {
      initialValues.propertyOptions = props.defaultPropertyOptions;
    }

    if (
      initialValues.propertyType === PropertyType.COMMERCIAL &&
      residentialCommercialTypes.includes(initialValues.commercialType as (typeof residentialCommercialTypes)[number])
    ) {
      initialValues.propertyPurpose = PropertyPurpose.INVESTMENT_PROPERTY;
    }

    resetForm({ values: initialValues });
  },
  { immediate: true },
);

watch(
  () => [values.propertyType, values.commercialType] as const,
  async ([propertyType, commercialType]) => {
    if (
      propertyType === PropertyType.COMMERCIAL &&
      residentialCommercialTypes.includes(commercialType as (typeof residentialCommercialTypes)[number])
    ) {
      await nextTick();
      setFieldValue('propertyPurpose', PropertyPurpose.INVESTMENT_PROPERTY);
    }
  },
  { flush: 'post' },
);

defineExpose({ update, cancel, isPropertyPurchaseRestricted, propertyOptions });
</script>

<template>
  <form>
    <div class="property-details-form">
      <div v-if="!props.isPropertyOptionsHidden" class="form-control">
        <BxField v-slot="{ field }" name="propertyOptions">
          <BxSelect
            :id="baseId + '-property-options'"
            :list-items="availablePropertyOptions"
            :option-tooltips="propertyOptionTooltips"
            v-bind="field"
            label="Property Purpose"
            label-type="floating"
            placeholder="Select property purpose..."
          />
        </BxField>
      </div>
      <BxField v-slot="{ field }" name="propertyType">
        <BxSelect
          :id="baseId + '-property-type'"
          :list-items="propertyTypeOptions"
          v-bind="field"
          class="form-control"
          label="Property Type"
          label-type="floating"
          placeholder="Select property type..."
        />
      </BxField>
      <div v-if="isPropertyPurchaseRestricted" class="form-control mt-6">
        <p>
          This property type requires a single security bridging loan, please select the button below to complete the
          single security application.
        </p>
      </div>
      <div v-if="values.propertyType === PropertyType.COMMERCIAL" class="form-control">
        <BxField v-slot="{ field }" name="commercialType">
          <BxSelect
            :id="baseId + '-commercial-type'"
            :list-items="commercialOptions"
            :option-tooltips="commercialTypeTooltips"
            v-bind="field"
            label="Commercial Type"
            label-type="floating"
            placeholder="Select commercial type..."
          />
        </BxField>
      </div>
      <BxField v-if="!isPropertyPurchaseRestricted" v-slot="{ field }" name="address">
        <BxAddressInput
          :id="baseId + '-property-address'"
          v-bind="field"
          class="form-control"
          label="Property Address"
          label-type="floating"
        />
      </BxField>
      <BxField
        v-if="!isPropertyPurchaseRestricted && !isResidentialCommercialType"
        v-slot="{ field }"
        name="propertyPurpose"
      >
        <BxRadio
          :id="baseId + '-property-used'"
          :options="propertyUsedOptions"
          v-bind="field"
          class="form-control"
          label="How is this property used?"
          label-type="default"
          block
        />
      </BxField>
      <BxField v-if="showPropertyLeasedQuestion" v-slot="{ field }" name="propertyLeased">
        <BxRadio
          :id="baseId + '-property-leased'"
          v-bind="field"
          class="form-control"
          label="Is the property currently leased?"
          label-type="default"
          boolean
        />
      </BxField>
      <BxField v-if="isResidentialCommercialType" v-slot="{ field }" name="idsType">
        <BxRadio
          :id="baseId + '-residence-type'"
          :options="residenceTypeOptions"
          v-bind="field"
          class="form-control"
          label="What is the residence type?"
          label-type="default"
        />
      </BxField>
      <div v-if="showRentalForSupportingProperty" class="my-4">
        <IncomingPropertyReceiveRentalIncome
          :id="baseId"
          label="Is this property expected to generate rental income?"
        />
        <IncomingPropertyExpectedRentalIncome v-if="values.receiveRentalIncome" :id="baseId" />
      </div>
      <PropertyToPurchaseFields
        v-if="values.propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE && !isPropertyPurchaseRestricted"
        :id="baseId"
        :contract-for-sale="values.contractForSale"
        :receive-rental-income="values.receiveRentalIncome"
        :property-purpose="values.propertyPurpose"
        :is-new-build="values.isNewBuild"
      />
      <PropertyToSellFields
        v-if="values.propertyOptions === PropertyDetailOption.PROPERTY_TO_SELL"
        :id="baseId"
        :sale-status="values.saleStatus"
      />
      <SupportingPropertyFields
        v-if="values.propertyOptions === PropertyDetailOption.SUPPORTING_PROPERTY"
        :id="baseId"
        :property-purpose="values.propertyPurpose"
        :is-new-build="values.isNewBuild"
      />
    </div>
    <BxErrorPanel v-show="!meta.valid && submitCount > 0" class="mt-6">
      Please review the error messages above.
    </BxErrorPanel>
  </form>
</template>

<style scoped>
.property-details-form :deep(.form-control) {
  padding-right: 2.5rem;
}
</style>
